/**
 * The typing behaviour behind a time field. While the field has focus the
 * text is a draft; it becomes the value on Enter or blur if it reads as a
 * time, and is dropped if it does not. The arrow keys move the time 15 minutes.
 */
import { useState, type KeyboardEvent } from 'react';

import { parseTimeOfDay, stepTimeOfDay } from './time-of-day';

export interface TimeFieldState {
  draft: string | null;
  open: boolean;
  /** What the draft reads as: undefined with no draft, null when it is not a time. */
  typed: number | null | undefined;
  /** The slot to highlight: the typed time when there is one, else the value. */
  current: number;
  invalid: boolean;
  focus: () => void;
  type: (text: string) => void;
  commit: () => void;
  pick: (minutes: number) => void;
  onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
}

export function useTimeField(value: number, onChange: (minutes: number) => void): TimeFieldState {
  const [draft, setDraft] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const typed = draft !== null && draft.trim() !== '' ? parseTimeOfDay(draft) : undefined;

  const close = () => {
    setDraft(null);
    setOpen(false);
  };
  const commit = () => {
    if (typed !== undefined && typed !== null) onChange(typed);
    close();
  };
  const pick = (minutes: number) => {
    onChange(minutes);
    close();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      onChange(stepTimeOfDay(typed ?? value, e.key === 'ArrowDown' ? 1 : -1));
      setDraft(null);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      commit();
    } else if (e.key === 'Escape') {
      close();
    }
  };

  return {
    draft,
    open,
    typed,
    current: typed ?? value,
    invalid: typed === null,
    focus: () => setOpen(true),
    type: (text) => {
      setDraft(text);
      setOpen(true);
    },
    commit,
    pick,
    onKeyDown,
  };
}
